/**
 * Design Tokens - Centralized design system configuration
 * Defines colors, typography, spacing, shadows, and other design constants
 * for use throughout the application
 */

export const tokens = {
  // ============================================
  // COLOR PALETTE
  // ============================================
  colors: {
    // Primary colors
    primary: {
      base: '#22c55e',        // Vibrant green - main action color
      dark: '#16a34a',        // Darker green
      light: '#4ade80',       // Lighter green
      lighter: '#86efac',     // Even lighter green
      faded: 'rgba(34, 197, 94, 0.1)',  // 10% opacity
    },

    // Secondary colors
    secondary: {
      base: '#f97316',        // Orange - accent color
      dark: '#ea580c',        // Darker orange
      light: '#fb923c',       // Lighter orange
      lighter: '#fdba74',     // Even lighter orange
      faded: 'rgba(249, 115, 22, 0.1)',
    },

    // Status colors
    status: {
      success: '#22c55e',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
    },

    // Neutral grayscale
    neutral: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },

    // Background and surface colors
    background: {
      base: '#0f172a',        // Deep dark navy - main background
      alt: '#0a0f1f',         // Even darker alternative
    },
    
    surface: {
      base: '#1e293b',        // Slate - primary surface (cards, containers)
      raised: '#334155',      // Slightly raised surface
      hover: 'rgba(226, 232, 240, 0.05)',  // Subtle hover state
    },

    // Text colors
    text: {
      primary: '#f8fafc',     // Main text (light)
      secondary: '#cbd5e1',   // Secondary text (medium)
      muted: '#94a3b8',       // Muted/weak text (light gray)
      inverse: '#0f172a',     // Inverse text (dark, for light backgrounds)
    },

    // Interactive states
    interactive: {
      focus: '#3b82f6',       // Focus ring color
      disabled: 'rgba(148, 163, 184, 0.3)',
      overlay: 'rgba(0, 0, 0, 0.5)',
    },

    // Semantic colors for borders
    border: {
      light: '#334155',
      base: '#475569',
      dark: '#64748b',
    },
  },

  // ============================================
  // TYPOGRAPHY
  // ============================================
  typography: {
    // Font families
    fonts: {
      primary: '"Plus Jakarta Sans", "Segoe UI", "Roboto", sans-serif',
      mono: '"Roboto Mono", "Monaco", "Courier New", monospace',
    },

    // Font sizes
    sizes: {
      xs: '0.75rem',      // 12px
      sm: '0.875rem',     // 14px
      base: '1rem',       // 16px
      lg: '1.125rem',     // 18px
      xl: '1.25rem',      // 20px
      '2xl': '1.5rem',    // 24px
      '3xl': '1.875rem',  // 30px
      '4xl': '2.25rem',   // 36px
    },

    // Font weights
    weights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },

    // Line heights
    lineHeights: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.75,
      loose: 2,
    },

    // Letter spacing
    letterSpacing: {
      tight: '-0.01em',
      normal: '0em',
      wide: '0.025em',
      wider: '0.05em',
    },
  },

  // ============================================
  // SPACING
  // ============================================
  spacing: {
    0: '0',
    1: '0.25rem',    // 4px
    2: '0.5rem',     // 8px
    3: '0.75rem',    // 12px
    4: '1rem',       // 16px
    6: '1.5rem',     // 24px
    8: '2rem',       // 32px
    10: '2.5rem',    // 40px
    12: '3rem',      // 48px
    16: '4rem',      // 64px
    20: '5rem',      // 80px
    24: '6rem',      // 96px
  },

  // ============================================
  // BORDER RADIUS
  // ============================================
  borderRadius: {
    none: '0',
    sm: '0.25rem',    // 4px - tight corners
    base: '0.5rem',   // 8px - standard corners
    md: '0.75rem',    // 12px - medium rounded
    lg: '1rem',       // 16px - rounded cards
    xl: '1.5rem',     // 24px - large rounded
    '2xl': '2rem',    // 32px - very rounded
    full: '9999px',   // Fully rounded (pills)
  },

  // ============================================
  // SHADOWS
  // ============================================
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    base: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',
    md: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
    xl: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
    
    // Elevation shadows (for depth)
    elevation: {
      1: '0 1px 3px 0 rgba(0, 0, 0, 0.4)',
      2: '0 4px 8px 0 rgba(0, 0, 0, 0.3)',
      3: '0 8px 16px 0 rgba(0, 0, 0, 0.25)',
      4: '0 12px 24px 0 rgba(0, 0, 0, 0.2)',
    },

    // Interactive shadows
    interactive: {
      hover: '0 6px 12px -2px rgba(0, 0, 0, 0.25)',
      active: '0 2px 4px -1px rgba(0, 0, 0, 0.3)',
      focus: '0 0 0 3px rgba(34, 197, 94, 0.1), 0 0 0 5px rgba(34, 197, 94, 0.3)',
    },
  },

  // ============================================
  // TRANSITIONS & ANIMATIONS
  // ============================================
  transitions: {
    // Duration
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '350ms',
      slower: '500ms',
    },

    // Timing
    timing: {
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
      linear: 'linear',
      smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },

    // Common transitions
    common: {
      all: 'all 250ms ease',
      background: 'background-color 250ms ease',
      color: 'color 250ms ease',
      opacity: 'opacity 250ms ease',
      transform: 'transform 250ms ease-in-out',
      shadow: 'box-shadow 250ms ease',
    },
  },

  // ============================================
  // BREAKPOINTS (Mobile-first)
  // ============================================
  breakpoints: {
    xs: '480px',      // Extra small phones
    sm: '640px',      // Small phones
    md: '768px',      // Tablets
    lg: '1024px',     // Small laptops
    xl: '1280px',     // Desktops
    '2xl': '1536px',  // Large desktops
  },

  // ============================================
  // Z-INDEX SCALE
  // ============================================
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    backdrop: 1040,
    offcanvas: 1050,
    modal: 1060,
    popover: 1070,
    tooltip: 1080,
    notification: 1090,
  },

  // ============================================
  // SIZE/DIMENSION SCALES
  // ============================================
  sizes: {
    // Icon sizes
    icon: {
      xs: '16px',
      sm: '20px',
      base: '24px',
      lg: '32px',
      xl: '40px',
      '2xl': '48px',
    },

    // Component heights
    componentHeight: {
      xs: '24px',       // Compact button
      sm: '32px',       // Small button
      base: '40px',     // Standard button/input
      lg: '48px',       // Large button
      xl: '56px',       // Extra large button
    },

    // Container widths
    container: {
      xs: '280px',
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
    },
  },

  // ============================================
  // OPACITY
  // ============================================
  opacity: {
    0: '0',
    5: '0.05',
    10: '0.1',
    20: '0.2',
    30: '0.3',
    40: '0.4',
    50: '0.5',
    60: '0.6',
    70: '0.7',
    80: '0.8',
    90: '0.9',
    100: '1',
  },
};

// ============================================
// THEME CONFIGURATION
// ============================================
export const THEME_CONFIG = {
  LIGHT: 'light',
  DARK: 'dark',
  DEFAULT: 'dark',
};

// ============================================
// RESPONSIVE UTILITIES
// ============================================
export const media = {
  xs: `@media (min-width: ${tokens.breakpoints.xs})`,
  sm: `@media (min-width: ${tokens.breakpoints.sm})`,
  md: `@media (min-width: ${tokens.breakpoints.md})`,
  lg: `@media (min-width: ${tokens.breakpoints.lg})`,
  xl: `@media (min-width: ${tokens.breakpoints.xl})`,
  '2xl': `@media (min-width: ${tokens.breakpoints['2xl']})`,

  // Mobile-first max-width queries if needed
  mobile: `@media (max-width: ${tokens.breakpoints.md})`,
  tablet: `@media (max-width: ${tokens.breakpoints.lg})`,
};

export default tokens;
